import { apiClient } from './client';

export interface KBArticleListItem {
  id: number;
  title: string;
  excerpt: string;
  labels: string[];
  updated_at: string;
}

export interface KBArticleDetail {
  id: number;
  title: string;
  content_html: string;
  labels: string[];
  updated_at: string;
}

export interface KBChatSource {
  article_id: number;
  title: string;
  url: string;
  similarity: number;
}

export interface KBChatResponse {
  answerable: boolean;
  answer: string | null;
  sources: KBChatSource[];
  error?: string;
}

export interface KBUnansweredQuestion {
  id: number;
  customer_id: number;
  question: string;
  route_context: string;
  labels_context: string[];
  status: 'open' | 'answered' | 'dismissed';
  answer: string;
  answered_at: string | null;
  assigned_labels: string[];
  created_at: string;
  merchant_name: string | null;
}

export const kbApi = {
  getArticles: async (route: string): Promise<{ articles: KBArticleListItem[]; labels: string[] }> => {
    const response = await apiClient.get('/kb/articles', { params: { route } });
    return response.data;
  },

  getArticle: async (id: number): Promise<KBArticleDetail> => {
    const response = await apiClient.get(`/kb/articles/${id}`);
    return response.data;
  },

  chat: async (question: string, route: string, labels?: string[], language?: string): Promise<KBChatResponse> => {
    const response = await apiClient.post('/kb/chat', { question, route, labels, language });
    return response.data;
  },

  // Admin endpoints
  getUnanswered: async (status?: string, limit = 50, offset = 0): Promise<{ questions: KBUnansweredQuestion[] }> => {
    const params: Record<string, string | number> = { limit, offset };
    if (status) params.status = status;
    const response = await apiClient.get('/admin/kb/unanswered', { params });
    return response.data;
  },

  answerQuestion: async (id: number, question: string, answer: string, assignedLabels: string[] = []): Promise<void> => {
    await apiClient.put(`/admin/kb/unanswered/${id}/answer`, {
      question,
      answer,
      assigned_labels: assignedLabels,
    });
  },

  updateQuestionLabels: async (id: number, assignedLabels: string[]): Promise<void> => {
    await apiClient.patch(`/admin/kb/unanswered/${id}/labels`, {
      assigned_labels: assignedLabels,
    });
  },

  dismissQuestion: async (id: number): Promise<void> => {
    await apiClient.put(`/admin/kb/unanswered/${id}/dismiss`);
  },

  deleteQuestion: async (id: number): Promise<void> => {
    await apiClient.delete(`/admin/kb/unanswered/${id}`);
  },

  updateQuestionStatus: async (id: number, status: string): Promise<void> => {
    await apiClient.patch(`/admin/kb/unanswered/${id}/status`, { status });
  },
};
